import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';

const Assessment = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // 5 Sliding Scale Questions (1-10) + 2 Multiple Choice
  const questions = [
    // Sliding Scale Questions
    {
      id: 'consistency',
      type: 'scale',
      question: 'How consistent are you with daily habits?',
      description: '1 = I struggle to maintain any routine, 10 = I rarely miss a day',
      min: 1,
      max: 10
    },
    {
      id: 'motivation',
      type: 'scale',
      question: 'How motivated do you feel to make a change right now?',
      description: '1 = Not very motivated, 10 = Extremely motivated',
      min: 1,
      max: 10
    },
    {
      id: 'discipline',
      type: 'scale',
      question: 'How would you rate your current self-discipline?',
      description: '1 = Very low, 10 = Very high',
      min: 1,
      max: 10
    },
    {
      id: 'follow_through',
      type: 'scale',
      question: 'When you set a goal, how often do you follow through?',
      description: '1 = Rarely finish, 10 = Almost always complete',
      min: 1,
      max: 10
    },
    {
      id: 'recovery',
      type: 'scale',
      question: 'After falling off track, how quickly do you get back on?',
      description: '1 = Takes weeks/months, 10 = Same or next day',
      min: 1,
      max: 10
    },
    // Multiple Choice Questions
    {
      id: 'biggest_challenge',
      type: 'choice',
      question: 'What is your biggest challenge with building habits?',
      options: [
        { value: 'starting', label: '🚀 Getting started' },
        { value: 'consistency', label: '📅 Staying consistent' },
        { value: 'motivation', label: '🔥 Maintaining motivation' },
        { value: 'time', label: '⏰ Finding time' },
        { value: 'overwhelm', label: '😰 Feeling overwhelmed' }
      ]
    },
    {
      id: 'peak_time',
      type: 'choice',
      question: 'When do you feel most productive and focused?',
      options: [
        { value: 'early_morning', label: '🌅 Early Morning (5-8am)' },
        { value: 'morning', label: '☀️ Morning (8-11am)' },
        { value: 'midday', label: '🌤️ Midday (11am-2pm)' },
        { value: 'afternoon', label: '🌇 Afternoon (2-5pm)' },
        { value: 'evening', label: '🌙 Evening (5-9pm)' },
        { value: 'night', label: '🌃 Night (9pm+)' }
      ]
    }
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleScaleChange = (value) => {
    setAnswers({ ...answers, [currentQ.id]: parseInt(value) });
  };

  const handleChoiceSelect = (value) => {
    setAnswers({ ...answers, [currentQ.id]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Save assessment results to backend
      await axios.post(`${API}/me/assessment`, { answers });
      // Navigate to Architect to start journey
      navigate('/architect');
    } catch (err) {
      console.error('Failed to save assessment:', err);
      // Still proceed to architect even if save fails
      navigate('/architect');
    }
  };

  const isAnswered = answers[currentQ.id] !== undefined;
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Self-Discipline Assessment</h1>
          <p className="text-sm text-gray-500 mt-1">Question {currentQuestion + 1} of {questions.length}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-200 h-2">
        <div 
          className="bg-emerald-500 h-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Question Card */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {currentQ.question}
          </h2>
          {currentQ.description && (
            <p className="text-gray-500 text-sm mb-6">{currentQ.description}</p>
          )}

          {/* Scale Input */}
          {currentQ.type === 'scale' && (
            <div className="space-y-4">
              <input
                type="range"
                min={currentQ.min}
                max={currentQ.max}
                value={answers[currentQ.id] || 5}
                onChange={(e) => handleScaleChange(e.target.value)}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>{currentQ.min}</span>
                <span className="text-2xl font-bold text-emerald-600">
                  {answers[currentQ.id] || 5}
                </span>
                <span>{currentQ.max}</span>
              </div>
            </div>
          )}

          {/* Choice Input */}
          {currentQ.type === 'choice' && (
            <div className="space-y-3">
              {currentQ.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleChoiceSelect(option.value)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition ${
                    answers[currentQ.id] === option.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-t p-4">
        <div className="max-w-lg mx-auto flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className={`px-6 py-3 rounded-xl font-medium ${
              currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            ← Back
          </button>

          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={!isAnswered || submitting}
              className={`px-8 py-3 rounded-xl font-semibold ${
                isAnswered && !submitting
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Saving...' : 'Complete Assessment →'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!isAnswered}
              className={`px-8 py-3 rounded-xl font-semibold ${
                isAnswered
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Assessment;
